import React, {useEffect, useRef, useState} from 'react'
import {
    Button,
    Chip,
    Collapse,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
} from "@mui/material"
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import SearchIcon from '@mui/icons-material/Search'
import {RowSearchParams} from "../../api/requests/RowRequests"
import {RowDataset, resolveDatasetName} from "../../api/enum/RowDataset"
import {ProjectJson} from "../../api/json/ProjectJson"
import SearchInput from "../form/SearchInput"
import DatasetFilterPopover from "../dataset/DatasetFilterPopover"

interface TableToolbarProps {
    project: ProjectJson
    onSearchParamChange: (params: Omit<RowSearchParams, 'sort'>) => void
    /** Primary table-level actions rendered at the far right of the toolbar (e.g. Add Rows, Transforms). */
    rightActions?: React.ReactNode
}

function TableToolbar({project, onSearchParamChange, rightActions}: TableToolbarProps) {
    const [dataset, setDataset] = useState<RowDataset>()
    const [searchValue, setSearchValue] = useState<string>()
    const [searchColumnId, setSearchFieldId] = useState<string>()

    const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null)
    const [searchExpanded, setSearchExpanded] = useState(false)
    // Tracks whether the collapsed "Search" button should be visible. We can't derive
    // this directly from searchExpanded because Collapse has an exit animation — during
    // the 150ms exit, we want the button to stay hidden so the two controls don't
    // overlap on screen. Flipped back to true via Collapse's onExited.
    const [showSearchButton, setShowSearchButton] = useState(true)
    const searchWrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const params: Omit<RowSearchParams, 'sort'> = {dataset}
        if (searchValue) {
            params.search = {value: searchValue}
            if (searchColumnId) {
                params.search.fieldId = searchColumnId
            }
        }
        onSearchParamChange(params)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataset, searchValue, searchColumnId])

    // Keep the search column mounted as long as either it's explicitly expanded or there's a value.
    const searchEffectivelyOpen = searchExpanded || !!searchValue

    const handleSearchBlur = () => {
        // Collapse on blur only when empty — can't just use onBlur directly because the
        // clear button inside SearchInput briefly defocuses before re-focusing, so give it a tick.
        setTimeout(() => {
            if (!searchValue && searchWrapperRef.current && !searchWrapperRef.current.contains(document.activeElement)) {
                setSearchExpanded(false)
            }
        }, 0)
    }

    return (
        <>
            <Stack direction="row"
                   spacing={2}
                   alignItems="center"
                   justifyContent="space-between"
                   sx={{
                       flexWrap: 'wrap',
                       rowGap: 1,
                       // Pin the row to the tallest control (the 40px search column) so
                       // expanding search doesn't bump the row height.
                       minHeight: 40,
                   }}>
                {/* Left cluster: filters, search, active-filter chips. */}
                <Stack direction="row"
                       spacing={1}
                       alignItems="center"
                       sx={{flexWrap: 'wrap', rowGap: 1}}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<FilterAltOutlinedIcon/>}
                        endIcon={<ArrowDropDownIcon/>}
                        onClick={(e) => setFilterAnchor(e.currentTarget)}
                    >
                        Filter
                    </Button>

                    {/* Collapsible search sits immediately next to the filter button
                        so it's visually grouped with the other toolbar controls. */}
                    <Stack ref={searchWrapperRef} direction="row" alignItems="center">
                        {showSearchButton && !searchEffectivelyOpen &&
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                startIcon={<SearchIcon/>}
                                onClick={() => {
                                    // Hide the button immediately so the expanding column
                                    // doesn't share space with it.
                                    setShowSearchButton(false)
                                    setSearchExpanded(true)
                                }}
                            >
                                Search
                            </Button>
                        }
                        <Collapse in={searchEffectivelyOpen}
                                  orientation="horizontal"
                                  timeout={150}
                                  unmountOnExit
                                  onExited={() => setShowSearchButton(true)}>
                            <Stack onBlur={handleSearchBlur}>
                                <SearchInput
                                    onThrottledChange={setSearchValue}
                                    autoFocus
                                    placeholder="Search…"
                                    sx={{width: 280}}
                                />
                            </Stack>
                        </Collapse>
                    </Stack>

                    {/* Active-filter chips */}
                    {dataset !== undefined &&
                        <Chip
                            label={`Dataset: ${resolveDatasetName(dataset)}`}
                            onClick={(e) => setFilterAnchor(e.currentTarget as HTMLElement)}
                            onDelete={() => setDataset(undefined)}
                            size="small"
                            variant="outlined"
                        />
                    }

                    {/* "Specific Column" select appears once the user has typed something.
                        Keep it inline after the chips so it doesn't fight with the
                        search column for horizontal space. */}
                    {searchValue &&
                        <FormControl size="small" sx={{minWidth: 150}}>
                            <InputLabel id="search-column-select-label">Specific Column</InputLabel>
                            <Select
                                labelId="search-column-select-label"
                                value={searchColumnId ?? ""}
                                label="Specific Column"
                                onChange={(event) => {
                                    const newValue = event.target.value
                                    setSearchFieldId(newValue === "" ? undefined : newValue)
                                }}
                            >
                                <MenuItem value="">All Columns</MenuItem>
                                {project.columns.map(column =>
                                    <MenuItem key={column.id} value={column.id}>{column.name}</MenuItem>,
                                )}
                            </Select>
                        </FormControl>
                    }
                </Stack>

                {/* Right cluster: primary table actions. */}
                {rightActions &&
                    <Stack direction="row"
                           spacing={1}
                           alignItems="center">
                        {rightActions}
                    </Stack>
                }
            </Stack>

            <DatasetFilterPopover
                anchorEl={filterAnchor}
                onClose={() => setFilterAnchor(null)}
                dataset={dataset}
                onDatasetChange={setDataset}
            />
        </>
    )
}

export default TableToolbar
